import {
  DdLogs,
  DdRum,
  DdSdkReactNative,
  ErrorSource,
} from '@datadog/mobile-react-native';
import { nanoid } from 'nanoid/non-secure';
import {
  configureReanimatedLogger,
  ReanimatedLogLevel,
} from 'react-native-reanimated';
import { useAuthStore } from 'stores/authStore';
import { getCustomerUidFromToken } from 'utils/accessTokenUtils';
import { isDatadogAvailable } from './commonUtils';

type LogType = 'error' | 'warn' | 'info';

configureReanimatedLogger({
  level: ReanimatedLogLevel.warn,
  strict: false,
});

export enum UserFlowName {
  CreateParkingSession = 'create_parking_session',
  EndParkingSession = 'end_parking_session',
}

export enum FlowStatus {
  Started = 'started',
  InProgress = 'in_progress',
  Updated = 'updated',
  Completed = 'completed',
  Failed = 'failed',
}

export enum UserActionType {
  Tap = 'tap',
}

export default class Logger {
  private static get LoggerImplementation() {
    if (isDatadogAvailable()) {
      return DdLogs;
    }
    return console;
  }
  private static formatSource = (message: string, source?: string) => {
    if (!source) {
      return `Aimo App | ${message}`;
    }
    return `(${source}): ${message}`;
  };

  /**
   * Error out a message to a logging provider, depending on environment.
   *
   * @param {string} message the message to log out
   * @param {string | undefined} source further specify the origin of the message for easier tracing
   * @param {Error | undefined} error the error to track in Datadog RUM
   */
  public static error = (
    message: string,
    source?: string,
    error?: Error,
    context?: object
  ) => {
    const formatted = Logger.formatSource(message, source);
    if (context === undefined) {
      this.LoggerImplementation.error(formatted);
    } else {
      this.LoggerImplementation.error(formatted, context);
    }

    if (isDatadogAvailable() && error) {
      this.addTrackingError(error);
    }
  };

  /**
   * Debug out a message to a logging provider, depending on environment.
   *
   * @param {string} message the message to log out
   * @param {string | undefined} source further specify the origin of the message for easier tracing
   */
  public static debug = (message: string, source?: string) => {
    this.LoggerImplementation.debug(Logger.formatSource(message, source));
  };

  /**
   * Info out a message to a logging provider, depending on environment.
   *
   * @param {string} message the message to log out
   * @param {string | undefined} source further specify the origin of the message for easier tracing
   */
  public static info = (message: string, source?: string, context?: object) => {
    const formatted = Logger.formatSource(message, source);
    if (context === undefined) {
      this.LoggerImplementation.info(formatted);
    } else {
      this.LoggerImplementation.info(formatted, context);
    }
  };

  /**
   * Warn out a message to a logging provider, depending on environment.
   *
   * @param {string} message the message to log out
   * @param {string | undefined} source further specify the origin of the message for easier tracing
   */
  public static warn = (message: string, source?: string) => {
    this.LoggerImplementation.warn(Logger.formatSource(message, source));
  };

  /**
   * Track error in Datadog
   *
   * @param {Error} error the error
   */
  public static addTrackingError = (error: Error) => {
    DdRum.addError(
      error.message,
      ErrorSource.CUSTOM,
      JSON.stringify(error.stack) || '',
      undefined,
      Date.now()
    );
  };

  public static setDatadogUser = async (uid?: string) => {
    if (isDatadogAvailable()) {
      const customerUid =
        uid ??
        getCustomerUidFromToken(useAuthStore.getState().accessToken ?? '');
      // Include the user information in Datadog
      await DdSdkReactNative.setUser({
        id: customerUid || '',
      });
    }
  };

  public static clearDatadogUser = async () => {
    if (isDatadogAvailable()) {
      // Clear the user information in Datadog
      await DdSdkReactNative.setUser({});
    }
  };

  public static logMessageWithCustomerUid = (
    logTitle: string,
    logDetails?: string,
    logType?: LogType
  ) => {
    const customerUid = getCustomerUidFromToken(
      useAuthStore.getState().accessToken ?? ''
    );
    const logMessage = `${logTitle} | CustomerUid: ${customerUid} ${
      logDetails ? `| ${logDetails}` : ''
    }`;
    switch (logType) {
      case 'error':
        Logger.error(logMessage);
        break;
      case 'warn':
        Logger.warn(logMessage);
        break;
      case 'info':
      default:
        Logger.info(logMessage);
        break;
    }
  };

  public static readonly logSafeMessage = (
    message: string,
    unsafeMessage?: string
  ) => {
    const isDev = process.env.NODE_ENV === 'development';
    const fullMessage = isDev ? `${message} | ${unsafeMessage ?? ''}` : message;
    this.info(fullMessage);
  };

  /**
   * Generate a unique ID to correlate all log events belonging to the same user flow instance.
   */
  public static generateFlowId = (): string => nanoid();

  /**
   * Log a user action (tap, press, etc.) with structured attributes.
   *
   * @param actionName  The name of the user flow (check `UserFlowName` enum)
   * @param actionType  The kind of interaction (check `UserActionType` enum)
   * @param context     Optional additional attributes
   */
  public static logUserAction = (
    actionName: UserFlowName,
    actionType: UserActionType,
    context?: object
  ) => {
    const customerUid = getCustomerUidFromToken(
      useAuthStore.getState().accessToken ?? ''
    );
    const actionContext = {
      action_name: actionName,
      action_type: actionType,
      customer_uid: customerUid || undefined,
      ...context,
    };
    const message = `Aimo App | User Action | ${actionType} | ${actionName}`;
    this.LoggerImplementation.info(message, actionContext);
  };

  /**
   * Log a user flow event with structured attributes for correlation.
   * Use the same flowId across all steps of a single flow instance so the logs
   * can be filtered/correlated in monitoring tool (i.e. Datadog) by `flow_id`.
   *
   * @param flowName  The name of the user flow (check `UserFlowName` enum)
   * @param flowId    A unique ID for this flow instance — generate once with `Logger.generateFlowId()`
   * @param status    The current status of the flow (check `FlowStatus` enum)
   * @param context   Optional additional attributes to attach to the log
   */
  public static logUserFlow = (
    flowName: UserFlowName,
    flowId: string,
    status: FlowStatus,
    context?: object
  ) => {
    const customerUid = getCustomerUidFromToken(
      useAuthStore.getState().accessToken ?? ''
    );
    const flowContext = {
      flow_name: flowName,
      flow_id: flowId,
      flow_status: status,
      customer_uid: customerUid || undefined,
      ...context,
    };

    const message = `Aimo App | User Flow | ${flowName} | ${status}`;

    if (status === FlowStatus.Failed) {
      this.LoggerImplementation.error(message, flowContext);
    } else {
      this.LoggerImplementation.info(message, flowContext);
    }
  };
}
